import {AttachmentSharedBase} from "@app/shared/components/attachments/attachments.interface";
import {AppConstants} from "@app/shared/util/app-constants";
import {Exclude, Transform, Type} from "class-transformer";
import moment, {Moment} from 'moment-timezone';
import {SustainabilityRatingMeasure, SustainabilityRatingMeasureId, SustainabilityRatingMeasureImpl} from '../measures/sustainability-rating-measures.interface';
import {SignedLink, SignedLinkImpl} from "@shared/api/model/signed-link";


export type SustainabilityRatingId = number;
export type SustainabilityRatingTypeId = number;
export type SustainabilityRatingAttachmentId = string;

export type MsraId = number;


export enum SustainabilityRatingsAggregateEntityEnum {
  ALL = 'all',
  SITE = 'site'
}


export interface MainSustainabilityRatingAttribute {
  msra_id: MsraId;
  name_key: string;
}


/** Only use for mapping */
export class MainSustainabilityRatingAttributeImpl implements MainSustainabilityRatingAttribute {
  msra_id: MsraId;
  name_key: string;
}


export interface SustainabilityRatingType {
  sustainability_rating_type_id: SustainabilityRatingTypeId;
  customer_id: number;
  name: string;
  msra_id: MsraId;
  main_sustainability_rating_attribute: MainSustainabilityRatingAttribute;
  max_score: number;
  min_score: number;
  shared: boolean;
  system: boolean;
  sustainability_rating_measure_ids: SustainabilityRatingMeasureId[];
  sustainability_rating_measures: SustainabilityRatingMeasure[];
  created_at: Moment;
  last_modified_at: Moment;
}




/** Only use for mapping */
export class SustainabilityRatingTypeImpl implements SustainabilityRatingType {
  sustainability_rating_type_id: SustainabilityRatingTypeId;
  @Type(() => moment)
  @Transform(({value}: { value: string | null }) => value ? moment.utc(value) : value, {toClassOnly: true})
  @Transform(({value}: { value: Moment | null }) => value ? value.toJSON() : value, {toPlainOnly: true})
  created_at: Moment;
  customer_id: number;
  @Type(() => moment)
  @Transform(({value}: { value: string | null }) => value ? moment.utc(value) : value, {toClassOnly: true})
  @Transform(({value}: { value: Moment | null }) => value ? value.toJSON() : value, {toPlainOnly: true})
  last_modified_at: Moment;
  @Type(() => MainSustainabilityRatingAttributeImpl)
  @Exclude({toPlainOnly: true})
  main_sustainability_rating_attribute: MainSustainabilityRatingAttribute;
  sustainability_rating_measure_ids: SustainabilityRatingMeasureId[];
  @Type(() => SustainabilityRatingMeasureImpl)
  @Exclude({toPlainOnly: true})
  sustainability_rating_measures: SustainabilityRatingMeasure[];
  max_score: number;
  min_score: number;
  msra_id: MsraId;
  name: string;
  shared: boolean;
  system: boolean;
}


export interface SustainabilityRatingAttachment extends AttachmentSharedBase<SignedLink> {
  sustainability_rating_attachment_id: SustainabilityRatingAttachmentId;
  sustainability_rating_id: SustainabilityRatingId;
}

export class SustainabilityRatingAttachmentImpl implements SustainabilityRatingAttachment {
  sustainability_rating_attachment_id: SustainabilityRatingAttachmentId;
  sustainability_rating_id: SustainabilityRatingId;
  author_id: string;
  file_name: string;
  @Type(() => SignedLinkImpl)
  link: SignedLink;
  mime_type: string;
}


export interface SustainabilityRatingAssociateRequestBody {
  sustainability_rating_attachments: {
    sustainability_rating_id: SustainabilityRatingId;
    file_name: string;
    upload_url: string;
  }[];
}

export interface SustainabilityRatingAggregated {
  sustainability_rating_type_id: SustainabilityRatingTypeId;
  sustainability_rating_type?: SustainabilityRatingType;
  score: number;
}


export interface SustainabilityRatingAggregatedBySite extends SustainabilityRatingAggregated {
  site_id: number;
}


export interface SustainabilityRating extends SustainabilityRatingAggregatedBySite {
  sustainability_rating_id?: SustainabilityRatingId;
  msra_value?: number;
  note?: string;
  period_start_date?: Moment;
  period_end_date?: Moment;
  expiry_date?: Moment;
  created_at?: Moment;
  last_modified_at?: Moment;
  attachments?: SustainabilityRatingAttachment[];
}


export class SustainabilityRatingImpl implements SustainabilityRating {
  sustainability_rating_id: SustainabilityRatingId;
  sustainability_rating_type_id: SustainabilityRatingTypeId;

  @Type(() => SustainabilityRatingTypeImpl)
  @Exclude({toPlainOnly: true})
  sustainability_rating_type: SustainabilityRatingType;

  site_id: number;
  msra_value: number;
  note: string;
  score: number;
  @Type(() => moment)
  @Transform(({value}: { value: string | null }) => value ? moment(value) : value, {toClassOnly: true})
  @Transform(({value}: { value: Moment | null }) => value ? value.format(AppConstants.DATE_FORMAT) : value, {toPlainOnly: true})
  period_start_date: Moment;

  @Type(() => moment)
  @Transform(({value}: { value: string | null }) => value ? (moment(value).subtract(1, 'day')) : value, {toClassOnly: true})
  @Transform(({value}: { value: Moment | null }) => {
    return value
      ? value.clone().add(1, 'day').format(AppConstants.DATE_FORMAT)
      : value
  }, {toPlainOnly: true})
  period_end_date: Moment;

  @Type(() => moment)
  @Transform(({value}: { value: string | null }) => value ? moment(value) : value, {toClassOnly: true})
  @Transform(({value}: { value: Moment | null }) => value ? value.format(AppConstants.DATE_FORMAT) : value, {toPlainOnly: true})
  expiry_date: Moment;

  @Type(() => moment)
  @Transform(({value}: { value: string | null }) => value ? moment.utc(value) : value, {toClassOnly: true})
  @Transform(({value}: { value: Moment | null }) => value ? value.toJSON() : value, {toPlainOnly: true})
  created_at: Moment;

  @Type(() => moment)
  @Transform(({value}: { value: string | null }) => value ? moment.utc(value) : value, {toClassOnly: true})
  @Transform(({value}: { value: Moment | null }) => value ? value.toJSON() : value, {toPlainOnly: true})
  last_modified_at: Moment;

  @Type(() => SustainabilityRatingAttachmentImpl)
  @Exclude({toPlainOnly: true})
  attachments: SustainabilityRatingAttachment[];
}

